import {
  decodeFunctionResult,
  encodeAbiParameters,
  encodeFunctionData,
  getAddress,
  hashMessage,
  recoverMessageAddress,
  recoverTypedDataAddress,
  serializeErc6492Signature,
  type Address,
  type Hex,
} from "viem";
import {
  getUserOperationHash,
  type UserOperation,
} from "viem/account-abstraction";

import {
  estimateMemberOperation,
  getMemberOperationGasPrice,
  getMemberOperationReceipt,
  getPendingMemberOperation,
  memberChainRequest,
  sendMemberOperation,
  retryPendingMemberOperation,
  sponsorMemberOperation,
} from "./member-wallet-transport";

export const WATCHER_ACCOUNT_FACTORY =
  "0x4be0ddfebca9a5a4a617dee4dece99e7c862dceb" as const;
export const WATCHER_ENTRY_POINT =
  "0x0000000071727De22E5E9d8BAf0edAc6f37da032" as const;
export const BASE_KARMA_TOKEN =
  "0x0910b5224d0d89f58885d0aeb20d56d1870127cf" as const;
export const BASE_USDC_TOKEN =
  "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913" as const;
export const WATCHER_ACCOUNT_ESTIMATION_SIGNATURE =
  "0xfffffffffffffffffffffffffffffff0000000000000000000000000000000007aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa1c" as const;

const BASE_CHAIN_ID = 8453;
const ACCOUNT_DATA = "0x" as const;
const WATCHER_ACCOUNT_CALL_GAS_BUFFER = BigInt(50_000);
const HASH = /^0x[0-9a-fA-F]{64}$/;
const SIGNATURE = /^0x[0-9a-fA-F]{130}$/;
const HEX = /^0x[0-9a-fA-F]*$/;

const factoryAbi = [
  {
    type: "function",
    name: "getAddress",
    stateMutability: "view",
    inputs: [
      { name: "adminSigner", type: "address" },
      { name: "data", type: "bytes" },
    ],
    outputs: [{ name: "account", type: "address" }],
  },
  {
    type: "function",
    name: "createAccount",
    stateMutability: "nonpayable",
    inputs: [
      { name: "admin", type: "address" },
      { name: "data", type: "bytes" },
    ],
    outputs: [{ name: "account", type: "address" }],
  },
  {
    type: "function",
    name: "accountImplementation",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "implementation", type: "address" }],
  },
] as const;

const accountAbi = [
  {
    type: "function",
    name: "execute",
    stateMutability: "payable",
    inputs: [
      { name: "target", type: "address" },
      { name: "value", type: "uint256" },
      { name: "calldata", type: "bytes" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "isAdmin",
    stateMutability: "view",
    inputs: [{ name: "signer", type: "address" }],
    outputs: [{ name: "allowed", type: "bool" }],
  },
  {
    type: "function",
    name: "entryPoint",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "entryPointAddress", type: "address" }],
  },
  {
    type: "function",
    name: "isValidSignature",
    stateMutability: "view",
    inputs: [
      { name: "hash", type: "bytes32" },
      { name: "signature", type: "bytes" },
    ],
    outputs: [{ name: "magicValue", type: "bytes4" }],
  },
] as const;

const entryPointAbi = [
  {
    type: "function",
    name: "getNonce",
    stateMutability: "view",
    inputs: [
      { name: "sender", type: "address" },
      { name: "key", type: "uint192" },
    ],
    outputs: [{ name: "nonce", type: "uint256" }],
  },
] as const;

export type AccountMessageTypedData = {
  domain: {
    name: "Account";
    version: "1";
    chainId: 8453;
    verifyingContract: Address;
  };
  primaryType: "AccountMessage";
  types: { AccountMessage: [{ name: "message"; type: "bytes" }] };
  message: { message: Hex };
};

export type ProviderNeutralSmartAccountOptions = {
  canonicalAccountAddress: string;
  originalAdminAddress: string;
  candidateSignerAddress: string;
  getMemberAuthToken: () => Promise<string>;
  signRawHash: (hash: Hex) => Promise<string>;
  signAccountMessage: (data: AccountMessageTypedData) => Promise<string>;
  receiptTimeoutMs?: number;
  receiptIntervalMs?: number;
  wait?: (milliseconds: number) => Promise<void>;
};

export type ProviderNeutralTransaction = {
  chainId: number;
  to: `0x${string}`;
  data: `0x${string}`;
  value: bigint;
  intentBindingToken?: string;
};

type UserOperationV07 = UserOperation<"0.7">;
type VerifiedSmartAccount = {
  canonical: Address;
  admin: Address;
  deployed: boolean;
  factoryData: Hex;
};

const counterfactualDeploymentTails = new Map<string, Promise<void>>();
const counterfactualDeploymentBarriers = new Map<string, Hex>();

function fail(message: string, details: Record<string, unknown> = {}): never {
  throw Object.assign(new Error(message), details);
}

function hexQuantity(value: bigint): Hex {
  if (value < BigInt(0)) fail("MEMBER_BUNDLER_RESPONSE_INVALID");
  return `0x${value.toString(16)}`;
}

function readHexQuantity(value: unknown): bigint {
  if (
    typeof value !== "string" ||
    !/^0x(?:0|[1-9a-fA-F][0-9a-fA-F]*)$/.test(value)
  ) {
    fail("MEMBER_BUNDLER_RESPONSE_INVALID");
  }
  return BigInt(value as string);
}

function asRpcUserOperation(operation: UserOperationV07) {
  return Object.fromEntries(
    Object.entries(operation as unknown as Record<string, unknown>)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]): [string, unknown] => [
        key,
        typeof value === "bigint" ? hexQuantity(value) : value,
      ]),
  );
}

function randomNonceKey(): bigint {
  if (!globalThis.crypto?.getRandomValues) {
    fail("MEMBER_SMART_ACCOUNT_RANDOMNESS_UNAVAILABLE");
  }
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(24));
  let key = BigInt(0);
  for (const byte of bytes) key = (key << BigInt(8)) | BigInt(byte);
  return key;
}

async function ethCall(token: string, to: Address, data: Hex): Promise<Hex> {
  const result = await memberChainRequest(
    BASE_CHAIN_ID,
    "eth_call",
    [{ to, data }, "latest"],
    token,
  );
  if (typeof result !== "string" || !HEX.test(result)) {
    fail("CHAIN_DATA_UNAVAILABLE");
  }
  return result as Hex;
}

async function readEntryPoint(
  token: string,
  address: Address,
): Promise<Address> {
  const data = encodeFunctionData({
    abi: accountAbi,
    functionName: "entryPoint",
  });
  const result = await ethCall(token, address, data);
  return decodeFunctionResult({
    abi: accountAbi,
    functionName: "entryPoint",
    data: result,
  });
}

export async function verifyProviderNeutralSmartAccount(
  options: ProviderNeutralSmartAccountOptions,
  memberAuthToken: string,
): Promise<VerifiedSmartAccount> {
  const canonical = getAddress(options.canonicalAccountAddress);
  const recordedAdmin = getAddress(options.originalAdminAddress);
  const candidate = getAddress(options.candidateSignerAddress);
  if (candidate !== recordedAdmin) fail("WALLET_CONTINUITY_MISMATCH");

  const predictionCall = encodeFunctionData({
    abi: factoryAbi,
    functionName: "getAddress",
    args: [recordedAdmin, ACCOUNT_DATA],
  });
  const prediction = decodeFunctionResult({
    abi: factoryAbi,
    functionName: "getAddress",
    data: await ethCall(
      memberAuthToken,
      WATCHER_ACCOUNT_FACTORY,
      predictionCall,
    ),
  });
  if (getAddress(prediction) !== canonical) fail("WALLET_CONTINUITY_MISMATCH");

  const adminCall = encodeFunctionData({
    abi: accountAbi,
    functionName: "isAdmin",
    args: [recordedAdmin],
  });
  const adminResult = await ethCall(memberAuthToken, canonical, adminCall);
  // eth_call against a counterfactual address returns empty bytes. A deployed
  // account must return a canonical bool and recognize the recorded admin.
  const deployed = adminResult !== "0x";
  let entryPoint: Address;
  if (deployed) {
    const isAdmin = decodeFunctionResult({
      abi: accountAbi,
      functionName: "isAdmin",
      data: adminResult,
    });
    if (!isAdmin) fail("WALLET_ADMIN_MISMATCH");
    entryPoint = await readEntryPoint(memberAuthToken, canonical);
  } else {
    const implementationCall = encodeFunctionData({
      abi: factoryAbi,
      functionName: "accountImplementation",
    });
    const implementation = decodeFunctionResult({
      abi: factoryAbi,
      functionName: "accountImplementation",
      data: await ethCall(
        memberAuthToken,
        WATCHER_ACCOUNT_FACTORY,
        implementationCall,
      ),
    });
    entryPoint = await readEntryPoint(memberAuthToken, implementation);
  }
  if (getAddress(entryPoint) !== getAddress(WATCHER_ENTRY_POINT)) {
    fail("MEMBER_SMART_ACCOUNT_ENTRY_POINT_MISMATCH");
  }
  if (deployed)
    counterfactualDeploymentBarriers.delete(canonical.toLowerCase());

  return {
    canonical,
    admin: recordedAdmin,
    deployed,
    factoryData: encodeFunctionData({
      abi: factoryAbi,
      functionName: "createAccount",
      args: [recordedAdmin, ACCOUNT_DATA],
    }),
  };
}

function validateSupportedTransfer(transaction: ProviderNeutralTransaction) {
  const target = getAddress(transaction.to);
  if (
    transaction.chainId !== BASE_CHAIN_ID ||
    transaction.value !== BigInt(0) ||
    (target !== getAddress(BASE_KARMA_TOKEN) &&
      target !== getAddress(BASE_USDC_TOKEN)) ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      transaction.intentBindingToken ?? "",
    ) ||
    !/^0xa9059cbb[0-9a-fA-F]{128}$/.test(transaction.data)
  ) {
    fail("MEMBER_SMART_ACCOUNT_CALL_REJECTED");
  }
  const recipientWord = transaction.data.slice(10, 74);
  const amountWord = transaction.data.slice(74, 138);
  if (
    !/^0{24}[0-9a-fA-F]{40}$/.test(recipientWord) ||
    BigInt(`0x${recipientWord}`) === BigInt(0) ||
    BigInt(`0x${amountWord}`) === BigInt(0)
  ) {
    fail("MEMBER_SMART_ACCOUNT_CALL_REJECTED");
  }
}

async function readNonce(token: string, sender: Address): Promise<bigint> {
  const call = encodeFunctionData({
    abi: entryPointAbi,
    functionName: "getNonce",
    args: [sender, randomNonceKey()],
  });
  const result = await ethCall(token, WATCHER_ENTRY_POINT, call);
  return decodeFunctionResult({
    abi: entryPointAbi,
    functionName: "getNonce",
    data: result,
  });
}

function parseGasEstimate(result: unknown) {
  const value = result as Record<string, unknown>;
  if (!value || typeof value !== "object")
    fail("MEMBER_BUNDLER_RESPONSE_INVALID");
  return {
    callGasLimit: readHexQuantity(value.callGasLimit),
    verificationGasLimit: readHexQuantity(value.verificationGasLimit),
    preVerificationGas: readHexQuantity(value.preVerificationGas),
    paymasterVerificationGasLimit:
      value.paymasterVerificationGasLimit === undefined
        ? undefined
        : readHexQuantity(value.paymasterVerificationGasLimit),
    paymasterPostOpGasLimit:
      value.paymasterPostOpGasLimit === undefined
        ? undefined
        : readHexQuantity(value.paymasterPostOpGasLimit),
  };
}

function parseGasPrice(result: unknown) {
  const tiers = result as Record<string, unknown>;
  const fast = tiers?.fast as Record<string, unknown> | undefined;
  if (!fast) fail("MEMBER_BUNDLER_RESPONSE_INVALID");
  return {
    maxFeePerGas: readHexQuantity(fast.maxFeePerGas),
    maxPriorityFeePerGas: readHexQuantity(fast.maxPriorityFeePerGas),
  };
}

async function signUserOperation(
  operation: UserOperationV07,
  options: ProviderNeutralSmartAccountOptions,
) {
  const hash = getUserOperationHash({
    chainId: BASE_CHAIN_ID,
    entryPointAddress: WATCHER_ENTRY_POINT,
    entryPointVersion: "0.7",
    userOperation: operation,
  });
  try {
    const signature = await options.signRawHash(hash);
    if (!SIGNATURE.test(signature))
      fail("MEMBER_SMART_ACCOUNT_SIGNATURE_INVALID");
    const recovered = await recoverMessageAddress({
      message: { raw: hash },
      signature: signature as Hex,
    });
    if (getAddress(recovered) !== getAddress(options.originalAdminAddress)) {
      fail("WALLET_ADMIN_MISMATCH");
    }
    return { hash, operation: { ...operation, signature: signature as Hex } };
  } catch (error) {
    throw Object.assign(
      error instanceof Error
        ? error
        : new Error("MEMBER_SMART_ACCOUNT_SIGNATURE_INVALID"),
      { candidateSigningAttempted: true },
    );
  }
}

async function withCounterfactualDeploymentLock<T>(
  account: Address,
  task: () => Promise<T>,
): Promise<T> {
  const key = account.toLowerCase();
  const previous = counterfactualDeploymentTails.get(key) ?? Promise.resolve();
  let release!: () => void;
  const gate = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.catch(() => undefined).then(() => gate);
  counterfactualDeploymentTails.set(key, tail);
  await previous.catch(() => undefined);
  try {
    return await task();
  } finally {
    release();
    if (counterfactualDeploymentTails.get(key) === tail) {
      counterfactualDeploymentTails.delete(key);
    }
  }
}

async function sendVerifiedProviderNeutralSmartAccountTransaction(
  options: ProviderNeutralSmartAccountOptions,
  transaction: ProviderNeutralTransaction,
  token: string,
  account: VerifiedSmartAccount,
): Promise<{
  transactionHash: `0x${string}`;
  userOperationHash: `0x${string}`;
}> {
  const [nonce, gasPriceResult] = await Promise.all([
    readNonce(token, account.canonical),
    getMemberOperationGasPrice(BASE_CHAIN_ID, token),
  ]);
  const gasPrice = parseGasPrice(gasPriceResult);
  const baseOperation: UserOperationV07 = {
    sender: account.canonical,
    nonce,
    callData: encodeFunctionData({
      abi: accountAbi,
      functionName: "execute",
      args: [getAddress(transaction.to), BigInt(0), transaction.data],
    }),
    callGasLimit: BigInt(0),
    verificationGasLimit: BigInt(0),
    preVerificationGas: BigInt(0),
    maxFeePerGas: gasPrice.maxFeePerGas,
    maxPriorityFeePerGas: gasPrice.maxPriorityFeePerGas,
    ...(account.deployed
      ? {}
      : { factory: WATCHER_ACCOUNT_FACTORY, factoryData: account.factoryData }),
    signature: WATCHER_ACCOUNT_ESTIMATION_SIGNATURE,
  };
  const estimate = parseGasEstimate(
    await estimateMemberOperation(
      BASE_CHAIN_ID,
      asRpcUserOperation(baseOperation),
      token,
    ),
  );
  let operation: UserOperationV07 = {
    ...baseOperation,
    callGasLimit: estimate.callGasLimit + WATCHER_ACCOUNT_CALL_GAS_BUFFER,
    verificationGasLimit: estimate.verificationGasLimit,
    preVerificationGas: estimate.preVerificationGas,
  };
  const sponsored = await sponsorMemberOperation(
    BASE_CHAIN_ID,
    asRpcUserOperation(operation),
    token,
  );
  if (
    typeof sponsored.paymaster !== "string" ||
    typeof sponsored.paymasterData !== "string" ||
    !/^0x[0-9a-fA-F]{40}$/.test(sponsored.paymaster) ||
    !HEX.test(sponsored.paymasterData)
  ) {
    fail("MEMBER_PAYMASTER_RESPONSE_INVALID");
  }
  operation = {
    ...operation,
    paymaster: getAddress(sponsored.paymaster),
    paymasterData: sponsored.paymasterData as Hex,
    callGasLimit:
      sponsored.callGasLimit === undefined
        ? operation.callGasLimit
        : sponsored.callGasLimit,
    verificationGasLimit:
      sponsored.verificationGasLimit ?? operation.verificationGasLimit,
    preVerificationGas:
      sponsored.preVerificationGas ?? operation.preVerificationGas,
    paymasterVerificationGasLimit:
      sponsored.paymasterVerificationGasLimit ??
      estimate.paymasterVerificationGasLimit ??
      BigInt(0),
    paymasterPostOpGasLimit:
      sponsored.paymasterPostOpGasLimit ??
      estimate.paymasterPostOpGasLimit ??
      BigInt(0),
  };
  const signed = await signUserOperation(operation, options);
  try {
    const submittedHash = await sendMemberOperation(
      BASE_CHAIN_ID,
      asRpcUserOperation(signed.operation),
      token,
      transaction.intentBindingToken!,
    );
    if (submittedHash.toLowerCase() !== signed.hash.toLowerCase()) {
      fail("MEMBER_BUNDLER_SUBMISSION_UNCERTAIN", {
        submittedUserOperationHash: submittedHash,
      });
    }

    return await waitForMemberOperationReceipt(options, token, submittedHash);
  } catch (error) {
    // From the first send attempt onward the operation may already be live.
    // Retain its deterministic hash and never permit a legacy retry.
    throw Object.assign(
      error instanceof Error
        ? error
        : new Error("MEMBER_BUNDLER_SUBMISSION_UNCERTAIN"),
      { userOperationHash: signed.hash },
    );
  }
}

async function waitForMemberOperationReceipt(
  options: ProviderNeutralSmartAccountOptions,
  token: string,
  hash: `0x${string}`,
  initialReceipt?: unknown,
): Promise<{
  transactionHash: `0x${string}`;
  userOperationHash: `0x${string}`;
}> {
  const deadline = Date.now() + (options.receiptTimeoutMs ?? 120_000);
  const wait =
    options.wait ??
    ((milliseconds: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, milliseconds)));
  let result = initialReceipt;
  while (Date.now() < deadline) {
    if (result === undefined)
      result = await getMemberOperationReceipt(BASE_CHAIN_ID, hash, token);
    if (result) {
      const receipt = result as {
        success?: unknown;
        userOpHash?: unknown;
        receipt?: { transactionHash?: unknown; status?: unknown };
      };
      if (
        typeof receipt.userOpHash !== "string" ||
        receipt.userOpHash.toLowerCase() !== hash.toLowerCase() ||
        typeof receipt.receipt?.transactionHash !== "string" ||
        !HASH.test(receipt.receipt.transactionHash) ||
        !["0x0", "0x1"].includes(String(receipt.receipt.status)) ||
        (receipt.success !== true && receipt.success !== false) ||
        (receipt.success === true && receipt.receipt.status !== "0x1")
      ) {
        fail("MEMBER_USER_OPERATION_RECEIPT_INVALID");
      }
      if (receipt.success === false) {
        fail("MEMBER_USER_OPERATION_REVERTED", {
          terminalUserOperation: true,
          userOperationHash: hash,
        });
      }
      return {
        transactionHash: receipt.receipt.transactionHash as `0x${string}`,
        userOperationHash: hash,
      };
    }
    await wait(options.receiptIntervalMs ?? 1_000);
    result = undefined;
  }
  fail("MEMBER_USER_OPERATION_PENDING", { userOperationHash: hash });
}

/** Explicit recovery of the server's exact saved operation; never maps it to a new transfer intent. */
export async function recoverPendingProviderNeutralSmartAccountOperation(
  options: ProviderNeutralSmartAccountOptions,
): Promise<{
  transactionHash: `0x${string}`;
  userOperationHash: `0x${string}`;
}> {
  const token = await options.getMemberAuthToken();
  const pending = await getPendingMemberOperation(BASE_CHAIN_ID, token);
  if (!pending || !HASH.test(pending.userOpHash))
    fail("MEMBER_USER_OPERATION_NOT_PENDING");
  const result = await retryPendingMemberOperation(BASE_CHAIN_ID, token);
  if (
    !result ||
    result.userOpHash.toLowerCase() !== pending.userOpHash.toLowerCase()
  ) {
    fail("MEMBER_USER_OPERATION_RECOVERY_INVALID");
  }
  if (result.status === "aborted") {
    fail("MEMBER_USER_OPERATION_PRODUCT_WINDOW_CLOSED", {
      terminalUserOperation: true,
      userOperationHash: pending.userOpHash,
    });
  }
  return await waitForMemberOperationReceipt(
    options,
    token,
    pending.userOpHash as `0x${string}`,
    result.status === "terminal" ? result.receipt : undefined,
  );
}

export async function sendProviderNeutralSmartAccountTransaction(
  options: ProviderNeutralSmartAccountOptions,
  transaction: ProviderNeutralTransaction,
): Promise<{
  transactionHash: `0x${string}`;
  userOperationHash: `0x${string}`;
}> {
  validateSupportedTransfer(transaction);
  const token = await options.getMemberAuthToken();
  const pending = await getPendingMemberOperation(BASE_CHAIN_ID, token);
  if (pending) {
    if (!HASH.test(pending.userOpHash)) fail("MEMBER_USER_OPERATION_PENDING");
    fail("MEMBER_USER_OPERATION_PENDING", {
      userOperationHash: pending.userOpHash,
    });
  }
  const account = await verifyProviderNeutralSmartAccount(options, token);
  if (account.deployed) {
    counterfactualDeploymentBarriers.delete(account.canonical.toLowerCase());
    return sendVerifiedProviderNeutralSmartAccountTransaction(
      options,
      transaction,
      token,
      account,
    );
  }
  return withCounterfactualDeploymentLock(account.canonical, async () => {
    // A concurrent first operation may have deployed the account while this
    // call waited. Refresh the account state before choosing factory fields.
    const refreshed = await verifyProviderNeutralSmartAccount(options, token);
    const key = refreshed.canonical.toLowerCase();
    if (refreshed.deployed) {
      counterfactualDeploymentBarriers.delete(key);
      return sendVerifiedProviderNeutralSmartAccountTransaction(
        options,
        transaction,
        token,
        refreshed,
      );
    }
    const pendingDeploymentHash = counterfactualDeploymentBarriers.get(key);
    if (pendingDeploymentHash) {
      fail("MEMBER_SMART_ACCOUNT_DEPLOYMENT_PENDING", {
        userOperationHash: pendingDeploymentHash,
      });
    }
    try {
      const result = await sendVerifiedProviderNeutralSmartAccountTransaction(
        options,
        transaction,
        token,
        refreshed,
      );
      // Keep later first-deployment calls blocked until a fresh chain read sees
      // the account. This also covers short RPC propagation lag after receipt.
      counterfactualDeploymentBarriers.set(key, result.userOperationHash);
      return result;
    } catch (error) {
      const operationHash =
        error &&
        typeof error === "object" &&
        "userOperationHash" in error &&
        typeof error.userOperationHash === "string" &&
        HASH.test(error.userOperationHash)
          ? (error.userOperationHash as Hex)
          : null;
      const terminal =
        error &&
        typeof error === "object" &&
        "terminalUserOperation" in error &&
        error.terminalUserOperation === true;
      if (operationHash && !terminal) {
        counterfactualDeploymentBarriers.set(key, operationHash);
      }
      throw error;
    }
  });
}

export async function signProviderNeutralSmartAccountMessage(
  options: ProviderNeutralSmartAccountOptions,
  message: string,
): Promise<string> {
  if (!message) fail("MEMBER_SIGNER_UNAVAILABLE");
  const token = await options.getMemberAuthToken();
  const account = await verifyProviderNeutralSmartAccount(options, token);
  const originalMessageHash = hashMessage(message);
  const data: AccountMessageTypedData = {
    domain: {
      name: "Account",
      version: "1",
      chainId: BASE_CHAIN_ID,
      verifyingContract: account.canonical,
    },
    primaryType: "AccountMessage",
    types: { AccountMessage: [{ name: "message", type: "bytes" }] },
    message: {
      message: encodeAbiParameters(
        [{ name: "message", type: "bytes32" }],
        [originalMessageHash],
      ),
    },
  };
  try {
    const signature = await options.signAccountMessage(data);
    if (!SIGNATURE.test(signature))
      fail("MEMBER_SMART_ACCOUNT_SIGNATURE_INVALID");
    const recovered = await recoverTypedDataAddress({
      ...data,
      signature: signature as Hex,
    });
    if (getAddress(recovered) !== account.admin) fail("WALLET_ADMIN_MISMATCH");
    if (account.deployed) {
      const validationCall = encodeFunctionData({
        abi: accountAbi,
        functionName: "isValidSignature",
        args: [originalMessageHash, signature as Hex],
      });
      const magicValue = decodeFunctionResult({
        abi: accountAbi,
        functionName: "isValidSignature",
        data: await ethCall(token, account.canonical, validationCall),
      });
      if (magicValue.toLowerCase() !== "0x1626ba7e") {
        fail("MEMBER_SMART_ACCOUNT_SIGNATURE_INVALID");
      }
      return signature;
    }
    return serializeErc6492Signature({
      address: WATCHER_ACCOUNT_FACTORY,
      data: account.factoryData,
      signature: signature as Hex,
    });
  } catch (error) {
    throw Object.assign(
      error instanceof Error
        ? error
        : new Error("MEMBER_SMART_ACCOUNT_SIGNATURE_INVALID"),
      { candidateSigningAttempted: true },
    );
  }
}
